import { cn } from "@/lib/utils";
import { KpiTile, type KpiTileProps } from "./KpiTile";

export type PerformanceTier = "TOP_PERFORMER" | "ESTABLISHED" | "GROWING" | "NEWCOMER";

const TIER_STYLES: Record<PerformanceTier, string> = {
  TOP_PERFORMER: "bg-warning/10 text-warning border-warning/30",
  ESTABLISHED: "bg-success/10 text-success border-success/30",
  GROWING: "bg-info/10 text-info border-info/30",
  NEWCOMER: "bg-muted text-muted-foreground border-border",
};

/** Subtle gradient backgrounds for the hero card, keyed by performance tier. */
const TIER_CARD_BG: Record<PerformanceTier, string> = {
  TOP_PERFORMER: "bg-gradient-to-br from-amber-50 to-orange-50/60 border-amber-200/80",
  ESTABLISHED:   "bg-gradient-to-br from-emerald-50 to-amber-50/60 border-emerald-200/80",
  GROWING:       "bg-gradient-to-br from-sky-50 to-indigo-50/60 border-indigo-200/80",
  NEWCOMER:      "bg-gradient-to-br from-slate-50 to-gray-50/60 border-slate-200",
};

/**
 * Exact same visual pattern as `TeacherAnalyticsPage.tsx`'s existing `TierBadge` —
 * colored pill with text + aria-label, never colour alone. Not a new design.
 */
function TierBadge({ tier }: { tier: PerformanceTier }) {
  const cls = TIER_STYLES[tier] ?? TIER_STYLES.NEWCOMER;
  // `tier` is typed required, but it arrives straight from an API payload and this badge renders
  // inside the teacher DASHBOARD's analytics card -- a summary missing this one field used to take
  // the whole dashboard down to the error boundary ("Something went wrong"), hiding every other
  // card on the page. Degrade to the neutral tier instead; the `cls` lookup above already did.
  const tierWords = (tier ?? "NEWCOMER").replace(/_/g, " ");
  return (
    <span
      aria-label={`Performance tier: ${tierWords}`}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}
    >
      {tierWords}
    </span>
  );
}

export interface TierHeroProps {
  tier: PerformanceTier;
  cohortSubject?: string;
  cohortMinimumMet: boolean;
  isCrashCourseOnly?: boolean;
  headlineKpis: [KpiTileProps, KpiTileProps];
  /** When true, renders without card border/shadow/bg — for use inside another card. */
  embedded?: boolean;
  className?: string;
}

/**
 * LOCKED cohort-standing sentence table (product decision from architecture review).
 * Checked in this priority order: NEWCOMER first, then crash-course-only, then
 * `!cohortMinimumMet`, then the tier-specific sentences — never silently show nothing,
 * a badge with no explanatory sentence would be confusing on a hero section built to
 * make tier prominent.
 *
 * | Case                                   | Sentence                                                        |
 * |------------------------------------------|-------------------------------------------------------------------|
 * | tier === NEWCOMER                        | "Your stats will appear here as students start finding you."     |
 * | crash-course-only teacher (any tier)     | "Your stats will appear here as your per-type comparisons build up." |
 * | cohortMinimumMet === false (else)         | "Not enough data in your cohort yet for a reliable comparison."  |
 * | tier === GROWING                          | "You're building momentum — keep replying quickly to climb."     |
 * | tier === ESTABLISHED                      | "You're at or above the median for {subject} teachers this month." |
 * | tier === TOP_PERFORMER                    | "You're in the top 25% of {subject} teachers this month."        |
 */
function cohortStandingSentence({
  tier,
  cohortSubject,
  cohortMinimumMet,
  isCrashCourseOnly,
}: Pick<TierHeroProps, "tier" | "cohortSubject" | "cohortMinimumMet" | "isCrashCourseOnly">): string {
  if (tier === "NEWCOMER") {
    return "Your stats will appear here as students start finding you.";
  }
  if (isCrashCourseOnly) {
    return "Your stats will appear here as your per-type comparisons build up.";
  }
  if (!cohortMinimumMet) {
    return "Not enough data in your cohort yet for a reliable comparison.";
  }
  if (tier === "GROWING") {
    return "You're building momentum — keep replying quickly to climb.";
  }

  // ESTABLISHED / TOP_PERFORMER — substitute {subject} where available, without an
  // awkward fallback pronoun when cohortSubject is absent.
  const subjectPhrase = cohortSubject ? `${cohortSubject} ` : "";
  if (tier === "ESTABLISHED") {
    return `You're at or above the median for ${subjectPhrase}teachers this month.`;
  }
  return `You're in the top 25% of ${subjectPhrase}teachers this month.`;
}

/**
 * Hero section for the Insights page: tier badge, a one-line cohort-standing
 * sentence, and exactly two headline KPI tiles, laid out prominently.
 */
export function TierHero({
  tier,
  cohortSubject,
  cohortMinimumMet,
  isCrashCourseOnly = false,
  headlineKpis,
  embedded = false,
  className,
}: TierHeroProps) {
  const sentence = cohortStandingSentence({ tier, cohortSubject, cohortMinimumMet, isCrashCourseOnly });

  return (
    <div className={cn(
      embedded
        ? "rounded-xl bg-slate-50 p-4"
        : cn("rounded-2xl border shadow-sm p-6", TIER_CARD_BG[tier]),
      className
    )}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <TierBadge tier={tier} />
      </div>
      <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{sentence}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {headlineKpis.map((kpi, i) => (
          <KpiTile key={i} {...kpi} embedded />
        ))}
      </div>
    </div>
  );
}

export default TierHero;
